import React, { useMemo, useState, useEffect } from "react";
import { Table, Card, CardBody, CardTitle, Badge, Button, Row, Col, Input, InputGroup, InputGroupText } from "reactstrap";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const PAGE_SIZE = 10;

const LeaseSettlementsPerformanceTable = ({
    reports,
    selectedManager,
    selectedStructure
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

    // Toggle Sort Helper
    const requestSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    // Sort Icon Helper
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <i className="bx bx-sort text-muted ms-1" style={{ fontSize: 12 }}></i>;
        return sortConfig.direction === 'asc'
            ? <i className="bx bx-caret-up text-white ms-1"></i>
            : <i className="bx bx-caret-down text-white ms-1"></i>;
    };

    const tableData = useMemo(() => {
        let rows = [];

        Object.entries(reports).forEach(([locationName, data]) => {
            // 1. Filters
            if (selectedManager !== "All Managers" && (data.account_manager || "").toLowerCase() !== selectedManager.toLowerCase()) return;
            if (selectedStructure !== "All Types" && (data.operating_structure || "").toLowerCase() !== selectedStructure.toLowerCase()) return;
            if (searchQuery) {
                rows = rows.filter(r => r.locationName.toLowerCase().includes(searchQuery.toLowerCase()));
            }

            const lease = data.lease_payments || {};

            // 2. Data Extraction Strategy
            if (lease.payments && lease.payments.length > 0) {
                // STRATEGY A: We have detailed payments (Use this if available)
                lease.payments.forEach(payment => {
                    rows.push({
                        id: payment.id,
                        locationName: locationName,
                        paymentDate: payment.payment_date,
                        meterAdj: payment.meter_adjustment || 0,
                        eventsAdj: payment.events_adjustment || 0,
                        valAdj: payment.validations_adjustment || 0,
                        amount: payment.amount || 0,
                        isSummary: false
                    });
                });
            } else if (lease.amount !== 0 && lease.amount !== undefined && lease.operating_structure === "Lease") {
                // STRATEGY B: Fallback to Summary Data (Works with your current JSON)
                // Only if amount is not 0 and it is a Lease
                rows.push({
                    id: `${data.id}-summary`,
                    locationName: locationName,
                    paymentDate: "Summary", // No date in summary data
                    meterAdj: 0, // Not available in summary
                    eventsAdj: 0, // Not available in summary
                    valAdj: 0, // Not available in summary
                    amount: lease.amount,
                    isSummary: true
                });
            }
        });

        // Sort: Real dates first, then summaries
        return rows.sort((a, b) => {
            if (sortConfig.key) {
                const valA = a[sortConfig.key];
                const valB = b[sortConfig.key];

                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            }

            // Default Sort: Real dates first, then summaries
            if (a.paymentDate === "Summary") return 1;
            if (b.paymentDate === "Summary") return -1;
            return new Date(b.paymentDate) - new Date(a.paymentDate);
        });

    }, [reports, selectedManager, selectedStructure, searchQuery, sortConfig]);

    // Reset page on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedManager, selectedStructure, searchQuery]);

    // Pagination
    const totalItems = tableData.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const currentTableData = tableData.slice(startIndex, startIndex + PAGE_SIZE);

    if (totalItems === 0) return null;

    // Export Function
    const handleExportCsv = () => {
        if (!tableData || tableData.length === 0) return;

        // Define Headers
        const headers = ["Location", "Payment Date", "Meter Adj.", "Events Adj.", "Validations Adj.", "Settlement Amount"];

        // Map Data to CSV Rows
        const csvRows = tableData.map(row => {
            // Helper to escape double quotes (CSV standard)
            const escape = (text) => `"${String(text || "").replace(/"/g, '""')}"`;

            return [
                escape(row.locationName),
                escape(row.paymentDate),
                row.meterAdj || 0,
                row.eventsAdj || 0,
                row.valAdj || 0,
                row.amount || 0
            ].join(",");
        });

        // Combine Headers and Rows with BOM for Excel compatibility
        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });

        // Trigger Download
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Lease_Settlements_Export_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Card style={{ backgroundColor: "#2f2f2f", borderRadius: 14, border: "1px solid rgba(255,255,255,0.1)" }}>
            <CardBody>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
                    <CardTitle className="h4 mb-0" style={{ color: "#fff", fontWeight: 700 }}>
                        Lease Settlements
                    </CardTitle>

                    <div className="d-flex gap-2 align-items-center" style={{ width: "100%", maxWidth: "500px" }}>
                        {/* Search Input */}
                        <InputGroup size="sm" style={{ flex: 1 }}>
                            <InputGroupText style={{ backgroundColor: "#333", border: "1px solid rgba(255,255,255,0.1)", color: "#ccc" }}>
                                <i className="bx bx-search"></i>
                            </InputGroupText>
                            <Input
                                placeholder="Search location..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ backgroundColor: "#333", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                            />
                        </InputGroup>

                        {/* Export Button */}
                        <Button
                            color="success"
                            size="sm"
                            onClick={handleExportCsv}
                            style={{ whiteSpace: "nowrap", fontWeight: 600 }}
                        >
                            <i className="bx bx-download me-1"></i> Export CSV
                        </Button>
                    </div>

                    <div style={{ color: "#ccc", fontSize: 13, whiteSpace: "nowrap" }}>
                        Showing {startIndex + 1} - {Math.min(startIndex + PAGE_SIZE, totalItems)} of {totalItems}
                    </div>
                </div>

                <div className="table-responsive">
                    <Table className="table-dark table-hover align-middle mb-0" style={{ fontSize: 14 }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ borderTopLeftRadius: 8, padding: "12px 16px" }}>Location</th>
                                <th style={{ padding: "12px 16px" }}>Payment Date</th>
                                <th
                                    className="text-end"
                                    style={{ padding: "12px 16px", cursor: "pointer", userSelect: "none" }}
                                    onClick={() => requestSort('meterAdj')}
                                >
                                    Meter Adj. {getSortIcon('meterAdj')}
                                </th>
                                <th
                                    className="text-end"
                                    style={{ padding: "12px 16px", cursor: "pointer", userSelect: "none" }}
                                    onClick={() => requestSort('eventsAdj')}
                                >
                                    Events Adj. {getSortIcon('eventsAdj')}
                                </th>
                                <th
                                    className="text-end"
                                    style={{ padding: "12px 16px", cursor: "pointer", userSelect: "none" }}
                                    onClick={() => requestSort('valAdj')}
                                >
                                    Validations Adj. {getSortIcon('valAdj')}
                                </th>
                                <th
                                    className="text-end"
                                    style={{ borderTopRightRadius: 8, padding: "12px 16px", cursor: "pointer", userSelect: "none" }}
                                    onClick={() => requestSort('amount')}
                                >
                                    Settlement Amount {getSortIcon('amount')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentTableData.map((row, index) => (
                                <tr key={row.id || index} style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                    <td style={{ fontWeight: 600, padding: "12px 16px" }}>
                                        {row.locationName}
                                    </td>
                                    <td style={{ padding: "12px 16px" }}>
                                        <Badge color={row.isSummary ? "info" : "secondary"} className="px-2 py-1" style={{ fontSize: "11px" }}>
                                            {formatDate(row.paymentDate)}
                                        </Badge>
                                    </td>
                                    <td className="text-end" style={{ color: "#ccc", padding: "12px 16px" }}>
                                        {formatCurrency(row.meterAdj)}
                                    </td>
                                    <td className="text-end" style={{ color: "#ccc", padding: "12px 16px" }}>
                                        {formatCurrency(row.eventsAdj)}
                                    </td>
                                    <td className="text-end" style={{ color: "#ccc", padding: "12px 16px" }}>
                                        {formatCurrency(row.valAdj)}
                                    </td>
                                    <td className="text-end" style={{ padding: "12px 16px" }}>
                                        <span
                                            style={{
                                                color: row.amount >= 0 ? "#22C55E" : "#f87171",
                                                fontWeight: 800,
                                                background: row.amount >= 0 ? "rgba(34, 197, 94, 0.1)" : "rgba(248, 113, 113, 0.1)",
                                                padding: "4px 8px",
                                                borderRadius: 6
                                            }}
                                        >
                                            ${formatCurrency(row.amount)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination Controls */}
                {totalItems > PAGE_SIZE && (
                    <Row className="align-items-center mt-3">
                        <Col className="text-end">
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                style={{ marginRight: 8 }}
                            >
                                Previous
                            </Button>
                            <span style={{ color: "#fff", margin: "0 10px", fontSize: 14 }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                color="secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                style={{ marginLeft: 8 }}
                            >
                                Next
                            </Button>
                        </Col>
                    </Row>
                )}
            </CardBody>
        </Card>
    );
};

export default LeaseSettlementsPerformanceTable;